/**
 * Tour activation, resume state, and the feature flag.
 *
 * Follows the codebase's flag idiom (`src/ai/openEndedAsk.ts`): an exported
 * default constant, a localStorage override for a single browser, and here also
 * a `?fg_tour=<slug>` opt-in so a sales email or a marketing CTA can turn one
 * tour on for one visitor without shipping anything.
 */

import { demoScenarioKey } from '../../data/demoSession'

/**
 * On since 2026-08-20: the first tour is authored, anchor-verified against
 * production, and its routes are live. Tours still start only from an explicit
 * `?fg_tour=<slug>` (or the once-per-browser first-login tour for real
 * customers) — this flag does not put an overlay on anyone who didn't follow
 * a tour link. Kill switch: localStorage `fusegrid_tour=0`.
 */
export const TOUR_DEFAULT_ENABLED = true

const LS_TOUR_KEY = 'fusegrid_tour'
export const TOUR_QUERY_PARAM = 'fg_tour'
export const TOUR_EMBED_PARAM = 'fg_tour_embed'
export const TOUR_ORIGIN_PARAM = 'fg_tour_origin'
export const TOUR_SELFCHECK_PARAM = 'fg_tour_selfcheck'
export const TOUR_AUTHOR_PARAM = 'fg_tour_author'
export const TOUR_VARIANT_PARAM = 'fg_tour_v'

/** Params `/demo` must carry across its `location.replace`, or the tour is lost. */
export const TOUR_FORWARD_PARAMS = [
  TOUR_QUERY_PARAM,
  TOUR_EMBED_PARAM,
  TOUR_ORIGIN_PARAM,
  TOUR_SELFCHECK_PARAM,
  TOUR_AUTHOR_PARAM,
  TOUR_VARIANT_PARAM,
] as const

export function isTourEnabled(): boolean {
  if (typeof window === 'undefined') return TOUR_DEFAULT_ENABLED
  try {
    const v = String(window.localStorage.getItem(LS_TOUR_KEY) || '').trim().toLowerCase()
    if (v === '1' || v === 'true' || v === 'on') return true
    if (v === '0' || v === 'false' || v === 'off') return false
  } catch {
    // storage unavailable (private mode / SSR shims) — fall through
  }
  return TOUR_DEFAULT_ENABLED
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/

export type TourActivation = {
  slug: string
  embed: boolean
  /** Parent origin for postMessage; never `'*'`, and never trusted blindly. */
  embedOrigin: string | null
  selfCheck: boolean
  author: boolean
  /** A/B arm from `?fg_tour_v=`; stamped on every event, nothing more. */
  variant: string | null
}

/** Origins allowed to receive tour events. An unknown parent gets nothing. */
export const TOUR_EMBED_ORIGIN_ALLOWLIST = [
  'https://fusegrid.io',
  'https://www.fusegrid.io',
  'http://localhost:4178',
  'http://127.0.0.1:4178',
]

export function readTourActivation(search: string = typeof window === 'undefined' ? '' : window.location.search): TourActivation | null {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search || '')
  } catch {
    return null
  }
  const slug = String(params.get(TOUR_QUERY_PARAM) || '').trim().toLowerCase()
  if (!SLUG_RE.test(slug)) return null
  const rawOrigin = String(params.get(TOUR_ORIGIN_PARAM) || '').trim()
  const rawVariant = String(params.get(TOUR_VARIANT_PARAM) || '').trim().toLowerCase()
  return {
    slug,
    embed: params.get(TOUR_EMBED_PARAM) === '1',
    embedOrigin: TOUR_EMBED_ORIGIN_ALLOWLIST.includes(rawOrigin) ? rawOrigin : null,
    selfCheck: params.get(TOUR_SELFCHECK_PARAM) === '1',
    author: params.get(TOUR_AUTHOR_PARAM) === '1',
    variant: /^[a-z0-9-]{1,24}$/.test(rawVariant) ? rawVariant : null,
  }
}

// ---------------------------------------------------------------------------
// Resume
// ---------------------------------------------------------------------------

export type TourProgress = {
  slug: string
  version: number
  stepId: string
  completedStepIds: string[]
  startedAt: number
}

/**
 * `sessionStorage`, not `localStorage`, and scoped to the demo scenario.
 *
 * A prospect returning a week later on a fresh link should get a fresh tour,
 * and per-tab storage means two demo sessions open at once cannot cross-talk.
 * Scoping to `demoScenarioKey()` matches how the free-module allowance already
 * behaves.
 */
const progressKey = (slug: string): string => `fg_tour_progress_${slug}_${demoScenarioKey() || 'anon'}`

export function readTourProgress(slug: string, version: number): TourProgress | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(progressKey(slug))
    if (!raw) return null
    const parsed = JSON.parse(raw) as TourProgress
    if (parsed?.slug !== slug) return null
    // A republished tour can have renamed or reordered steps; resuming into the
    // old one lands on a step that no longer exists. Start clean instead.
    if (parsed?.version !== version) return null
    return parsed
  } catch {
    return null
  }
}

export function writeTourProgress(progress: TourProgress): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(progressKey(progress.slug), JSON.stringify(progress))
  } catch {
    /* storage full or unavailable — resume is a convenience, not a contract */
  }
}

export function clearTourProgress(slug: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(progressKey(slug))
  } catch {
    /* ignore */
  }
}
